/**
 * Redux slice for submitting a new gig shift request.
 *
 * Wraps POST /api/gig/requestShift. Mirrors server-side validation up front so
 * the UI can surface field-level errors without a round trip. On success the
 * backend returns { message, gigRequestId, timestamp }.
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API_POST } from '../../services';
import { ENDPOINTS } from '../../constants/endpoints';
import {
    GigShiftRequest,
    GigShiftRequestResponse,
    GigValidationErrorResponse,
} from '../../types/gigApi';

interface GigRequestShiftState {
    lastSubmitted: GigShiftRequest | null;
    lastResponse: GigShiftRequestResponse | null;
    loading: boolean;
    success: boolean;
    validationErrors: string[];
    error: string | null;
}

const initialState: GigRequestShiftState = {
    lastSubmitted: null,
    lastResponse: null,
    loading: false,
    success: false,
    validationErrors: [],
    error: null,
};

// ========================================
// CLIENT-SIDE VALIDATION
// ========================================
export const validateGigShiftRequest = (payload: GigShiftRequest): string[] => {
    const errors: string[] = [];

    if (!payload.workdayId?.trim()) {
        errors.push('workdayId is required');
    }

    const schedule = payload.requestSchedule ?? [];
    schedule.forEach((day, idx) => {
        const label = `requestSchedule[${idx}]`;
        if (!day.requestDate || Number.isNaN(Date.parse(day.requestDate))) {
            errors.push(`${label}.requestDate must be a valid ISO date`);
        }
        if (!day.startTime?.trim()) {
            errors.push(`${label}.startTime is required`);
        }
        if (!day.endTime?.trim()) {
            errors.push(`${label}.endTime is required`);
        }
        if (!Array.isArray(day.skillCd) || day.skillCd.length === 0) {
            errors.push(`${label}.skillCd must contain at least one skill code`);
        }
    });

    return errors;
};

// ========================================
// ASYNC THUNKS
// ========================================
/**
 * Submit a gig shift request.
 * Uses: POST /api/gig/requestShift
 */
export const submitGigShiftRequest = createAsyncThunk<
    { payload: GigShiftRequest; response: GigShiftRequestResponse },
    GigShiftRequest,
    { rejectValue: { message: string; errors: string[] } }
>(
    'gigRequestShift/submit',
    async (payload, { rejectWithValue }) => {
        const clientErrors = validateGigShiftRequest(payload);
        if (clientErrors.length > 0) {
            return rejectWithValue({
                message: 'Invalid gig shift request',
                errors: clientErrors,
            });
        }

        try {
            const response = await API_POST(ENDPOINTS.GIG_REQUEST_SHIFT, payload);
            return { payload, response: response as GigShiftRequestResponse };
        } catch (error: any) {
            const data = error?.data ?? error?.response?.data;
            const validation = data as GigValidationErrorResponse | undefined;
            return rejectWithValue({
                message: validation?.message || error?.message || 'Failed to submit gig shift request',
                errors: validation?.errors ?? [],
            });
        }
    }
);

// ========================================
// SLICE
// ========================================
const gigRequestShiftSlice = createSlice({
    name: 'gigRequestShift',
    initialState,
    reducers: {
        resetGigRequestShift: (state) => {
            state.lastSubmitted = null;
            state.lastResponse = null;
            state.loading = false;
            state.success = false;
            state.validationErrors = [];
            state.error = null;
        },
        clearError: (state) => {
            state.error = null;
            state.validationErrors = [];
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(submitGigShiftRequest.pending, (state) => {
                state.loading = true;
                state.success = false;
                state.error = null;
                state.validationErrors = [];
            })
            .addCase(submitGigShiftRequest.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.lastSubmitted = action.payload.payload;
                state.lastResponse = action.payload.response;
            })
            .addCase(submitGigShiftRequest.rejected, (state, action) => {
                state.loading = false;
                state.success = false;
                state.error = action.payload?.message ?? (action.error?.message || 'Failed to submit gig shift request');
                state.validationErrors = action.payload?.errors ?? [];
            });
    },
});

export const { resetGigRequestShift, clearError } = gigRequestShiftSlice.actions;
export default gigRequestShiftSlice.reducer;
